import { Box, Button, Typography } from '@mui/material';
import { Trans } from '@lingui/macro';
import { Entitlement } from '@/entitlements/enums';
import { upsellRedirectDialogStore } from '@/stores/upsell-redirect-dialog-store';
import { ReactNode } from 'react';
import { Nullable } from '@/types';
import { checkForEntitlements } from '@/entitlements/utils';
import { EntityType } from '@/__generated__/API';
import { pascalCase } from '@/utils/pascal-case';

export type VideoBrickedProps = {
  showUpsell: (forced: boolean) => void;
  entityRequiresUltra: boolean;
  entityRequiresCHPC: boolean;
  title?: ReactNode | null | undefined;
  subtitle?: ReactNode | null | undefined;
  cta?: ReactNode | null | undefined;
  requiredEntitlements?: Nullable<string>[] | null;
  entityType?: EntityType;
};

export function VideoBricked({
  showUpsell,
  entityRequiresUltra,
  entityRequiresCHPC,
  title,
  subtitle,
  cta,
  requiredEntitlements,
  entityType
}: VideoBrickedProps) {
  const { isEntitled: isMembershipEntitlement } = checkForEntitlements(
    [Entitlement.MASTERY, Entitlement.PRO, Entitlement.STARTER],
    requiredEntitlements
  );

  const handleClickUpgrade = () => {
    rudderanalytics.track('Click', {
      element_id: 'show-upsell-button',
      parent_id: 'Play & Brick',
      type: 'BUTTON'
    });
    if (entityRequiresUltra) {
      upsellRedirectDialogStore.set.entitlement(Entitlement.ULTRA);
      upsellRedirectDialogStore.set.isOpen(true);
      return;
    } else if (entityRequiresCHPC) {
      upsellRedirectDialogStore.set.entitlement(Entitlement.CHPC_NEW);
      upsellRedirectDialogStore.set.isOpen(true);
      return;
    } else {
      showUpsell(true);
    }
  };

  const contentType = entityType ? pascalCase(entityType).toLowerCase() : '';

  return (
    <Box
      id="bricked-overlay"
      className="bricked-overlay"
      sx={{
        position: 'absolute',
        left: '0',
        top: '0',
        width: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        color: 'white',
        zIndex: 1000,
        borderRadius: '6px 6px 0 0'
      }}
    >
      <Box
        className="bricked-overlay-container"
        display="flex"
        height="100%"
        width="100%"
        sx={{
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Box
          maxWidth={{ xs: '95%', md: '80%' }}
          display="flex"
          flexDirection="column"
          justifyItems="center"
        >
          <Typography
            className="bricked-overlay-title"
            fontWeight="600"
            textAlign="center"
          >
            {title ||
              (isMembershipEntitlement ? (
                <Trans id="bricked.title">
                  Feeling inspired?
                  <br />
                  Get the full experience!
                </Trans>
              ) : (
                <Trans id="bricked.non-membership-title">
                  Oops… this {contentType} is missing from your collection.
                </Trans>
              ))}
          </Typography>

          <Typography
            className="brick-overlay-description"
            fontWeight="600"
            textAlign="center"
            sx={{ mt: { xs: 1, md: 2 } }}
          >
            {subtitle ||
              (isMembershipEntitlement ? (
                <Trans id="bricked.description">
                  Upgrade plan to continue.
                </Trans>
              ) : (
                <Trans id="bricked.non-membership-description">
                  Fix that. Unlock it now to Own it Forever!
                </Trans>
              ))}
          </Typography>

          <Box
            display="flex"
            flexDirection="row"
            flexGrow={1}
            justifyContent="center"
            sx={{ mt: { xs: 2, md: 4 } }}
            onClick={handleClickUpgrade}
          >
            <Button onClick={handleClickUpgrade}>
              {cta ||
                (isMembershipEntitlement ? (
                  <Trans id="common.upgrade-your-plan">Upgrade Your Plan</Trans>
                ) : (
                  <Trans id="common.purchase-now">Purchase Now</Trans>
                ))}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
